import { type QueryState } from "../hooks/useQueryWithState";
import { formatDollars } from "../lib/translate";
import { signalLifecycleState } from "../lib/tradingDisplay";
import { EmptyState, ErrorState, LoadingState } from "./states";
import type { ExecutionSignal } from "./ExecutionAccountabilityPanel";

export interface AgentAccountabilityConfig {
  agent: string;
  label: string;
  description: string;
  sourceLabel?: string;
  showCandlePatterns?: boolean;
  showCandlePatternPerformance?: boolean;
  showCandlePatternSignalLedger?: boolean;
  showDirectionPerformance?: boolean;
  showSessionPerformance?: boolean;
  showSymbolPerformance?: boolean;
  showFbdTrapEvidence?: boolean;
  showMrReversionEvidence?: boolean;
}

export interface CandlePatternBucket {
  name: string;
  count: number;
  open: number;
  wins: number;
  losses: number;
  breakevens: number;
  totalPnl: number;
  winRate: number | null;
}

export interface CandlePatternSignalDetail {
  id: string;
  createdAt: number;
  date: string;
  symbol: string;
  session: string;
  direction: string;
  triggerPath: string | null;
  patterns: string[];
  patternScore: number | null;
  lifecycle: ReturnType<typeof signalLifecycleState>;
  pnlDollars: number | null;
}

export interface DirectionPerformanceBucket {
  direction: "long" | "short";
  total: number;
  open: number;
  wins: number;
  losses: number;
  breakevens: number;
  totalPnl: number;
  winRate: number | null;
}

export interface SessionPerformanceBucket {
  session: "asia" | "us" | "other";
  label: string;
  total: number;
  open: number;
  wins: number;
  losses: number;
  breakevens: number;
  totalPnl: number;
  winRate: number | null;
}

export interface SymbolPerformanceBucket {
  symbol: string;
  total: number;
  open: number;
  wins: number;
  losses: number;
  breakevens: number;
  totalPnl: number;
  winRate: number | null;
}

export interface FbdTrapEvidenceSummary {
  reclaimSignals: number;
  absorptionTrapSignals: number;
  trapScoreCount: number;
  avgTrapScore: number | null;
  maxTrapScore: number | null;
  fbdFlowScoreCount: number;
  avgFbdFlowScore: number | null;
}

export interface FbdPathPerformanceBucket {
  path: "reclaim" | "absorption_trap";
  label: string;
  total: number;
  open: number;
  wins: number;
  losses: number;
  breakevens: number;
  totalPnl: number;
  winRate: number | null;
  trapScoreCount: number;
  avgTrapScore: number | null;
}

export interface MrReversionEvidenceSummary {
  extensionStddevCount: number;
  avgExtensionStddev: number | null;
  maxExtensionStddev: number | null;
  confirmationScoreCount: number;
  avgConfirmationScore: number | null;
  exhaustionScoreCount: number;
  avgExhaustionScore: number | null;
  volumeExhaustionSignals: number;
  rrTp1Count: number;
  avgRrTp1: number | null;
}

export interface MrExtensionPerformanceBucket {
  bucket: "lt2" | "twoToThree" | "gte3";
  label: string;
  total: number;
  open: number;
  wins: number;
  losses: number;
  breakevens: number;
  totalPnl: number;
  winRate: number | null;
  extensionCount: number;
  avgExtensionStddev: number | null;
}

export interface AgentAccountabilitySummary {
  agent: string;
  total: number;
  open: number;
  closed: number;
  wins: number;
  losses: number;
  breakevens: number;
  longs: number;
  shorts: number;
  asia: number;
  us: number;
  otherSession: number;
  totalPnl: number;
  winRate: number | null;
  candlePatternSignals: number;
  candlePatterns: CandlePatternBucket[];
  candlePatternSignalDetails: CandlePatternSignalDetail[];
  maxCandlePatternScore: number | null;
  directionPerformance: DirectionPerformanceBucket[];
  sessionPerformance: SessionPerformanceBucket[];
  symbolPerformance: SymbolPerformanceBucket[];
  fbdTrapEvidence: FbdTrapEvidenceSummary;
  fbdPathPerformance: FbdPathPerformanceBucket[];
  mrReversionEvidence: MrReversionEvidenceSummary;
  mrExtensionPerformance: MrExtensionPerformanceBucket[];
}

function emptyCandlePatternBucket(name: string): CandlePatternBucket {
  return {
    name,
    count: 0,
    open: 0,
    wins: 0,
    losses: 0,
    breakevens: 0,
    totalPnl: 0,
    winRate: null,
  };
}

function applyCandlePatternPerformance(
  bucket: CandlePatternBucket,
  signal: ExecutionSignal,
  lifecycle: ReturnType<typeof signalLifecycleState>,
) {
  bucket.count += 1;
  if (lifecycle === "open") bucket.open += 1;
  if (lifecycle === "win") bucket.wins += 1;
  if (lifecycle === "loss") bucket.losses += 1;
  if (lifecycle === "breakeven") bucket.breakevens += 1;
  if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
    bucket.totalPnl += signal.pnl_dollars;
  }
}

function finalizeCandlePatternBucket(bucket: CandlePatternBucket): CandlePatternBucket {
  const decisive = bucket.wins + bucket.losses;
  return {
    ...bucket,
    winRate: decisive > 0 ? bucket.wins / decisive : null,
  };
}

function emptyFbdTrapEvidence(): FbdTrapEvidenceSummary {
  return {
    reclaimSignals: 0,
    absorptionTrapSignals: 0,
    trapScoreCount: 0,
    avgTrapScore: null,
    maxTrapScore: null,
    fbdFlowScoreCount: 0,
    avgFbdFlowScore: null,
  };
}

function emptyMrReversionEvidence(): MrReversionEvidenceSummary {
  return {
    extensionStddevCount: 0,
    avgExtensionStddev: null,
    maxExtensionStddev: null,
    confirmationScoreCount: 0,
    avgConfirmationScore: null,
    exhaustionScoreCount: 0,
    avgExhaustionScore: null,
    volumeExhaustionSignals: 0,
    rrTp1Count: 0,
    avgRrTp1: null,
  };
}

function emptyDirectionPerformance(direction: "long" | "short"): DirectionPerformanceBucket {
  return {
    direction,
    total: 0,
    open: 0,
    wins: 0,
    losses: 0,
    breakevens: 0,
    totalPnl: 0,
    winRate: null,
  };
}

function emptySessionPerformance(session: "asia" | "us" | "other", label: string): SessionPerformanceBucket {
  return {
    session,
    label,
    total: 0,
    open: 0,
    wins: 0,
    losses: 0,
    breakevens: 0,
    totalPnl: 0,
    winRate: null,
  };
}

function emptySymbolPerformance(symbol: string): SymbolPerformanceBucket {
  return {
    symbol,
    total: 0,
    open: 0,
    wins: 0,
    losses: 0,
    breakevens: 0,
    totalPnl: 0,
    winRate: null,
  };
}

function emptyFbdPathPerformance(path: "reclaim" | "absorption_trap", label: string): FbdPathPerformanceBucket {
  return {
    path,
    label,
    total: 0,
    open: 0,
    wins: 0,
    losses: 0,
    breakevens: 0,
    totalPnl: 0,
    winRate: null,
    trapScoreCount: 0,
    avgTrapScore: null,
  };
}

function emptyMrExtensionPerformance(bucket: "lt2" | "twoToThree" | "gte3", label: string): MrExtensionPerformanceBucket {
  return {
    bucket,
    label,
    total: 0,
    open: 0,
    wins: 0,
    losses: 0,
    breakevens: 0,
    totalPnl: 0,
    winRate: null,
    extensionCount: 0,
    avgExtensionStddev: null,
  };
}

function emptySummary(agent: string): AgentAccountabilitySummary {
  return {
    agent,
    total: 0,
    open: 0,
    closed: 0,
    wins: 0,
    losses: 0,
    breakevens: 0,
    longs: 0,
    shorts: 0,
    asia: 0,
    us: 0,
    otherSession: 0,
    totalPnl: 0,
    winRate: null,
    candlePatternSignals: 0,
    candlePatterns: [],
    candlePatternSignalDetails: [],
    maxCandlePatternScore: null,
    directionPerformance: [emptyDirectionPerformance("long"), emptyDirectionPerformance("short")],
    sessionPerformance: [
      emptySessionPerformance("asia", "Asia"),
      emptySessionPerformance("us", "US"),
      emptySessionPerformance("other", "Other"),
    ],
    symbolPerformance: [],
    fbdTrapEvidence: emptyFbdTrapEvidence(),
    fbdPathPerformance: [
      emptyFbdPathPerformance("reclaim", "Reclaim"),
      emptyFbdPathPerformance("absorption_trap", "Absorption trap"),
    ],
    mrReversionEvidence: emptyMrReversionEvidence(),
    mrExtensionPerformance: [
      emptyMrExtensionPerformance("lt2", "<2σ"),
      emptyMrExtensionPerformance("twoToThree", "2–3σ"),
      emptyMrExtensionPerformance("gte3", "≥3σ"),
    ],
  };
}

function mrExtensionBucket(extensionStddev: number): "lt2" | "twoToThree" | "gte3" {
  const absExtension = Math.abs(extensionStddev);
  if (absExtension < 2) return "lt2";
  if (absExtension < 3) return "twoToThree";
  return "gte3";
}

function isAbsorptionTrapSignal(signal: ExecutionSignal): boolean {
  return signal.absorption_trap === true || (signal.signal_type || "").toLowerCase() === "absorption_trap";
}

function signalTriggerPath(agent: string, signal: ExecutionSignal): string | null {
  if (agent === "fbd") return isAbsorptionTrapSignal(signal) ? "Absorption trap" : "Reclaim";
  if (agent === "mr") return "Mean reversion";
  return null;
}

function buildCandlePatternSignalDetail(
  agent: string,
  signal: ExecutionSignal,
  patterns: string[],
  lifecycle: ReturnType<typeof signalLifecycleState>,
): CandlePatternSignalDetail {
  return {
    id: signal._id,
    createdAt: typeof signal.created_at === "number" && Number.isFinite(signal.created_at) ? signal.created_at : 0,
    date: signal.date || "—",
    symbol: (signal.symbol || "UNKNOWN").toUpperCase(),
    session: signal.session || "—",
    direction: signal.direction || "—",
    triggerPath: signalTriggerPath(agent, signal),
    patterns,
    patternScore: typeof signal.reversal_pattern_score === "number" && Number.isFinite(signal.reversal_pattern_score)
      ? signal.reversal_pattern_score
      : null,
    lifecycle,
    pnlDollars: typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars) ? signal.pnl_dollars : null,
  };
}

function applyDirectionPerformance(
  bucket: DirectionPerformanceBucket,
  signal: ExecutionSignal,
  lifecycle: ReturnType<typeof signalLifecycleState>,
) {
  bucket.total += 1;
  if (lifecycle === "open") bucket.open += 1;
  if (lifecycle === "win") bucket.wins += 1;
  if (lifecycle === "loss") bucket.losses += 1;
  if (lifecycle === "breakeven") bucket.breakevens += 1;
  if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
    bucket.totalPnl += signal.pnl_dollars;
  }
}

function finalizeDirectionPerformance(bucket: DirectionPerformanceBucket): DirectionPerformanceBucket {
  const decisive = bucket.wins + bucket.losses;
  return {
    ...bucket,
    winRate: decisive > 0 ? bucket.wins / decisive : null,
  };
}

function applySessionPerformance(
  bucket: SessionPerformanceBucket,
  signal: ExecutionSignal,
  lifecycle: ReturnType<typeof signalLifecycleState>,
) {
  bucket.total += 1;
  if (lifecycle === "open") bucket.open += 1;
  if (lifecycle === "win") bucket.wins += 1;
  if (lifecycle === "loss") bucket.losses += 1;
  if (lifecycle === "breakeven") bucket.breakevens += 1;
  if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
    bucket.totalPnl += signal.pnl_dollars;
  }
}

function finalizeSessionPerformance(bucket: SessionPerformanceBucket): SessionPerformanceBucket {
  const decisive = bucket.wins + bucket.losses;
  return {
    ...bucket,
    winRate: decisive > 0 ? bucket.wins / decisive : null,
  };
}

function applySymbolPerformance(
  bucket: SymbolPerformanceBucket,
  signal: ExecutionSignal,
  lifecycle: ReturnType<typeof signalLifecycleState>,
) {
  bucket.total += 1;
  if (lifecycle === "open") bucket.open += 1;
  if (lifecycle === "win") bucket.wins += 1;
  if (lifecycle === "loss") bucket.losses += 1;
  if (lifecycle === "breakeven") bucket.breakevens += 1;
  if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
    bucket.totalPnl += signal.pnl_dollars;
  }
}

function finalizeSymbolPerformance(bucket: SymbolPerformanceBucket): SymbolPerformanceBucket {
  const decisive = bucket.wins + bucket.losses;
  return {
    ...bucket,
    winRate: decisive > 0 ? bucket.wins / decisive : null,
  };
}

function unitIntervalScore(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 && value <= 1 ? value : null;
}

function applyFbdPathPerformance(
  bucket: FbdPathPerformanceBucket,
  signal: ExecutionSignal,
  lifecycle: ReturnType<typeof signalLifecycleState>,
  trapScore: number | null,
) {
  bucket.total += 1;
  if (lifecycle === "open") bucket.open += 1;
  if (lifecycle === "win") bucket.wins += 1;
  if (lifecycle === "loss") bucket.losses += 1;
  if (lifecycle === "breakeven") bucket.breakevens += 1;
  if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
    bucket.totalPnl += signal.pnl_dollars;
  }
  if (trapScore !== null) {
    bucket.trapScoreCount += 1;
  }
}

function finalizeFbdPathPerformance(bucket: FbdPathPerformanceBucket, trapScoreSum: number): FbdPathPerformanceBucket {
  const decisive = bucket.wins + bucket.losses;
  return {
    ...bucket,
    winRate: decisive > 0 ? bucket.wins / decisive : null,
    avgTrapScore: bucket.trapScoreCount > 0 ? trapScoreSum / bucket.trapScoreCount : null,
  };
}

function applyMrExtensionPerformance(
  bucket: MrExtensionPerformanceBucket,
  signal: ExecutionSignal,
  lifecycle: ReturnType<typeof signalLifecycleState>,
) {
  bucket.total += 1;
  if (lifecycle === "open") bucket.open += 1;
  if (lifecycle === "win") bucket.wins += 1;
  if (lifecycle === "loss") bucket.losses += 1;
  if (lifecycle === "breakeven") bucket.breakevens += 1;
  if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
    bucket.totalPnl += signal.pnl_dollars;
  }
  bucket.extensionCount += 1;
}

function finalizeMrExtensionPerformance(
  bucket: MrExtensionPerformanceBucket,
  extensionStddevSum: number,
): MrExtensionPerformanceBucket {
  const decisive = bucket.wins + bucket.losses;
  return {
    ...bucket,
    winRate: decisive > 0 ? bucket.wins / decisive : null,
    avgExtensionStddev: bucket.extensionCount > 0 ? extensionStddevSum / bucket.extensionCount : null,
  };
}

export function parseCandlePatterns(raw: string | null | undefined): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) {
      return parsed.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    }
  } catch {
    // Fall through to a permissive split for legacy comma-delimited rows.
  }
  return raw
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);
}

export function buildAgentAccountabilitySummary(
  signals: ExecutionSignal[],
  agent: string,
): AgentAccountabilitySummary {
  const target = agent.toLowerCase();
  const summary = emptySummary(target);
  const candlePatternPerformance = new Map<string, CandlePatternBucket>();
  const directionPerformance = {
    long: emptyDirectionPerformance("long"),
    short: emptyDirectionPerformance("short"),
  } satisfies Record<"long" | "short", DirectionPerformanceBucket>;
  const sessionPerformance = {
    asia: emptySessionPerformance("asia", "Asia"),
    us: emptySessionPerformance("us", "US"),
    other: emptySessionPerformance("other", "Other"),
  } satisfies Record<"asia" | "us" | "other", SessionPerformanceBucket>;
  const symbolPerformance = new Map<string, SymbolPerformanceBucket>();
  const fbdPathPerformance = {
    reclaim: emptyFbdPathPerformance("reclaim", "Reclaim"),
    absorption_trap: emptyFbdPathPerformance("absorption_trap", "Absorption trap"),
  } satisfies Record<"reclaim" | "absorption_trap", FbdPathPerformanceBucket>;
  const fbdPathTrapScoreSums = {
    reclaim: 0,
    absorption_trap: 0,
  } satisfies Record<"reclaim" | "absorption_trap", number>;
  const mrExtensionPerformance = {
    lt2: emptyMrExtensionPerformance("lt2", "<2σ"),
    twoToThree: emptyMrExtensionPerformance("twoToThree", "2–3σ"),
    gte3: emptyMrExtensionPerformance("gte3", "≥3σ"),
  } satisfies Record<"lt2" | "twoToThree" | "gte3", MrExtensionPerformanceBucket>;
  const mrExtensionStddevSums = {
    lt2: 0,
    twoToThree: 0,
    gte3: 0,
  } satisfies Record<"lt2" | "twoToThree" | "gte3", number>;
  let trapScoreSum = 0;
  let fbdFlowScoreSum = 0;
  let extensionStddevSum = 0;
  let confirmationScoreSum = 0;
  let exhaustionScoreSum = 0;
  let rrTp1Sum = 0;

  for (const signal of signals) {
    if ((signal.agent || "").toLowerCase() !== target) continue;

    summary.total += 1;

    const lifecycle = signalLifecycleState(signal);
    if (lifecycle === "open") summary.open += 1;
    else summary.closed += 1;
    if (lifecycle === "win") summary.wins += 1;
    if (lifecycle === "loss") summary.losses += 1;
    if (lifecycle === "breakeven") summary.breakevens += 1;

    const direction = (signal.direction || "").toLowerCase();
    if (direction === "long") {
      summary.longs += 1;
      applyDirectionPerformance(directionPerformance.long, signal, lifecycle);
    } else if (direction === "short") {
      summary.shorts += 1;
      applyDirectionPerformance(directionPerformance.short, signal, lifecycle);
    }

    const session = (signal.session || "").toLowerCase();
    const sessionKey = session === "asia" ? "asia" : session === "us" ? "us" : "other";
    if (sessionKey === "asia") summary.asia += 1;
    else if (sessionKey === "us") summary.us += 1;
    else summary.otherSession += 1;
    applySessionPerformance(sessionPerformance[sessionKey], signal, lifecycle);

    const symbol = (signal.symbol || "UNKNOWN").toUpperCase();
    const symbolBucket = symbolPerformance.get(symbol) ?? emptySymbolPerformance(symbol);
    symbolPerformance.set(symbol, symbolBucket);
    applySymbolPerformance(symbolBucket, signal, lifecycle);

    if (typeof signal.pnl_dollars === "number" && Number.isFinite(signal.pnl_dollars)) {
      summary.totalPnl += signal.pnl_dollars;
    }

    if (target === "fbd") {
      const pathKey = isAbsorptionTrapSignal(signal) ? "absorption_trap" : "reclaim";
      if (pathKey === "absorption_trap") summary.fbdTrapEvidence.absorptionTrapSignals += 1;
      else summary.fbdTrapEvidence.reclaimSignals += 1;
      const trapScore = unitIntervalScore(signal.trap_score);
      applyFbdPathPerformance(fbdPathPerformance[pathKey], signal, lifecycle, trapScore);

      if (trapScore !== null) {
        trapScoreSum += trapScore;
        fbdPathTrapScoreSums[pathKey] += trapScore;
        summary.fbdTrapEvidence.trapScoreCount += 1;
        summary.fbdTrapEvidence.maxTrapScore = Math.max(
          summary.fbdTrapEvidence.maxTrapScore ?? 0,
          trapScore,
        );
      }
      const fbdFlowScore = unitIntervalScore(signal.fbd_flow_score);
      if (fbdFlowScore !== null) {
        fbdFlowScoreSum += fbdFlowScore;
        summary.fbdTrapEvidence.fbdFlowScoreCount += 1;
      }
    }

    if (target === "mr") {
      if (typeof signal.extension_stddev === "number" && Number.isFinite(signal.extension_stddev)) {
        const extensionBucket = mrExtensionBucket(signal.extension_stddev);
        extensionStddevSum += signal.extension_stddev;
        mrExtensionStddevSums[extensionBucket] += signal.extension_stddev;
        applyMrExtensionPerformance(mrExtensionPerformance[extensionBucket], signal, lifecycle);
        summary.mrReversionEvidence.extensionStddevCount += 1;
        summary.mrReversionEvidence.maxExtensionStddev = Math.max(
          summary.mrReversionEvidence.maxExtensionStddev ?? 0,
          signal.extension_stddev,
        );
      }
      if (typeof signal.confirmation_score === "number" && Number.isFinite(signal.confirmation_score)) {
        confirmationScoreSum += signal.confirmation_score;
        summary.mrReversionEvidence.confirmationScoreCount += 1;
      }
      if (typeof signal.exhaustion_score === "number" && Number.isFinite(signal.exhaustion_score)) {
        exhaustionScoreSum += signal.exhaustion_score;
        summary.mrReversionEvidence.exhaustionScoreCount += 1;
      }
      if (signal.volume_exhaustion === true) summary.mrReversionEvidence.volumeExhaustionSignals += 1;
      if (typeof signal.rr_tp1 === "number" && Number.isFinite(signal.rr_tp1)) {
        rrTp1Sum += signal.rr_tp1;
        summary.mrReversionEvidence.rrTp1Count += 1;
      }
    }

    const patterns = parseCandlePatterns(signal.reversal_patterns);
    if (patterns.length > 0) {
      summary.candlePatternSignals += 1;
      summary.candlePatternSignalDetails.push(buildCandlePatternSignalDetail(target, signal, patterns, lifecycle));
      for (const pattern of patterns) {
        const patternBucket = candlePatternPerformance.get(pattern) ?? emptyCandlePatternBucket(pattern);
        candlePatternPerformance.set(pattern, patternBucket);
        applyCandlePatternPerformance(patternBucket, signal, lifecycle);
      }
    }
    if (typeof signal.reversal_pattern_score === "number" && Number.isFinite(signal.reversal_pattern_score)) {
      summary.maxCandlePatternScore = Math.max(summary.maxCandlePatternScore ?? 0, signal.reversal_pattern_score);
    }
  }

  const decisive = summary.wins + summary.losses;
  summary.winRate = decisive > 0 ? summary.wins / decisive : null;
  summary.candlePatterns = [...candlePatternPerformance.values()]
    .map(finalizeCandlePatternBucket)
    .sort((a, b) => b.count - a.count || b.totalPnl - a.totalPnl || a.name.localeCompare(b.name));
  summary.candlePatternSignalDetails.sort((a, b) => b.createdAt - a.createdAt || b.date.localeCompare(a.date));
  summary.directionPerformance = [
    finalizeDirectionPerformance(directionPerformance.long),
    finalizeDirectionPerformance(directionPerformance.short),
  ];
  summary.sessionPerformance = [
    finalizeSessionPerformance(sessionPerformance.asia),
    finalizeSessionPerformance(sessionPerformance.us),
    finalizeSessionPerformance(sessionPerformance.other),
  ];
  summary.symbolPerformance = [...symbolPerformance.values()]
    .map(finalizeSymbolPerformance)
    .sort((a, b) => b.total - a.total || b.totalPnl - a.totalPnl || a.symbol.localeCompare(b.symbol));
  summary.fbdPathPerformance = [
    finalizeFbdPathPerformance(fbdPathPerformance.reclaim, fbdPathTrapScoreSums.reclaim),
    finalizeFbdPathPerformance(fbdPathPerformance.absorption_trap, fbdPathTrapScoreSums.absorption_trap),
  ];
  summary.fbdTrapEvidence.avgTrapScore = summary.fbdTrapEvidence.trapScoreCount > 0
    ? trapScoreSum / summary.fbdTrapEvidence.trapScoreCount
    : null;
  summary.fbdTrapEvidence.avgFbdFlowScore = summary.fbdTrapEvidence.fbdFlowScoreCount > 0
    ? fbdFlowScoreSum / summary.fbdTrapEvidence.fbdFlowScoreCount
    : null;
  summary.mrReversionEvidence.avgExtensionStddev = summary.mrReversionEvidence.extensionStddevCount > 0
    ? extensionStddevSum / summary.mrReversionEvidence.extensionStddevCount
    : null;
  summary.mrReversionEvidence.avgConfirmationScore = summary.mrReversionEvidence.confirmationScoreCount > 0
    ? confirmationScoreSum / summary.mrReversionEvidence.confirmationScoreCount
    : null;
  summary.mrReversionEvidence.avgExhaustionScore = summary.mrReversionEvidence.exhaustionScoreCount > 0
    ? exhaustionScoreSum / summary.mrReversionEvidence.exhaustionScoreCount
    : null;
  summary.mrReversionEvidence.avgRrTp1 = summary.mrReversionEvidence.rrTp1Count > 0
    ? rrTp1Sum / summary.mrReversionEvidence.rrTp1Count
    : null;
  summary.mrExtensionPerformance = [
    finalizeMrExtensionPerformance(mrExtensionPerformance.lt2, mrExtensionStddevSums.lt2),
    finalizeMrExtensionPerformance(mrExtensionPerformance.twoToThree, mrExtensionStddevSums.twoToThree),
    finalizeMrExtensionPerformance(mrExtensionPerformance.gte3, mrExtensionStddevSums.gte3),
  ];
  return summary;
}

function formatPct(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${Math.round(value * 100)}%`;
}

function formatSigma(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)}σ`;
}

function formatRMultiple(value: number | null): string {
  if (value === null || !Number.isFinite(value)) return "—";
  return `${value.toFixed(2)}R`;
}

function SummaryMetric({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="agent-acc-metric">
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function formatSignalLifecycle(lifecycle: ReturnType<typeof signalLifecycleState>): string {
  if (lifecycle === "open") return "Open";
  if (lifecycle === "win") return "Win";
  if (lifecycle === "loss") return "Loss";
  return "Breakeven";
}

function CandlePatternSummary({
  summary,
  showPerformance = false,
  showSignalLedger = false,
}: {
  summary: AgentAccountabilitySummary;
  showPerformance?: boolean;
  showSignalLedger?: boolean;
}) {
  return (
    <div className="agent-acc-patterns" aria-label="TA candle patterns">
      <div className="agent-acc-section-title">TA candle patterns</div>
      <p>
        LOG-ONLY reversal evidence from <code>reversal_patterns</code>. Pattern signals: {summary.candlePatternSignals}
        {summary.maxCandlePatternScore !== null ? ` · max score ${summary.maxCandlePatternScore.toFixed(2)}` : ""}
      </p>
      {summary.candlePatterns.length > 0 && showPerformance ? (
        <div className="agent-acc-detail-grid" aria-label="Pattern performance">
          {summary.candlePatterns.map((pattern) => (
            <div key={pattern.name} className="agent-acc-detail-card">
              <strong>{pattern.name}</strong>
              <span>{formatSignalCount(pattern.count)} · {pattern.open} open</span>
              <span>{pattern.wins}W / {pattern.losses}L / {pattern.breakevens}BE</span>
              <span>{formatPct(pattern.winRate)} WR · {formatDollars(pattern.totalPnl)}</span>
            </div>
          ))}
        </div>
      ) : summary.candlePatterns.length > 0 ? (
        <div className="agent-acc-pattern-chips">
          {summary.candlePatterns.map((pattern) => (
            <span key={pattern.name} className="agent-acc-pattern-chip">
              {pattern.name} ×{pattern.count}
            </span>
          ))}
        </div>
      ) : (
        <small>No TA candle pattern detected in this window.</small>
      )}
      {summary.candlePatternSignalDetails.length > 0 && showSignalLedger ? (
        <>
          <div className="agent-acc-section-title agent-acc-subsection-title">Pattern trigger ledger</div>
          <div className="agent-acc-detail-grid" aria-label="Pattern trigger ledger">
            {summary.candlePatternSignalDetails.slice(0, 10).map((signal) => (
              <div key={signal.id} className="agent-acc-detail-card">
                <strong>{signal.symbol} · {signal.date}</strong>
                <span>{signal.session.toUpperCase()} · {signal.direction.toLowerCase()}</span>
                {signal.triggerPath ? <span>{signal.triggerPath}</span> : null}
                <span>{signal.patterns.join(", ")}</span>
                <span>{formatSignalLifecycle(signal.lifecycle)} · {signal.pnlDollars === null ? "P&L pending" : formatDollars(signal.pnlDollars)}</span>
                {signal.patternScore !== null ? <small>pattern score {signal.patternScore.toFixed(2)}</small> : null}
              </div>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

function formatSignalCount(count: number): string {
  return `${count} ${count === 1 ? "signal" : "signals"}`;
}

function DirectionPerformanceSummary({ summary }: { summary: AgentAccountabilitySummary }) {
  return (
    <div className="agent-acc-direction-perf" aria-label="Direction performance">
      <div className="agent-acc-section-title">Direction performance</div>
      <p>Read-only ORB split by direction from the same scoped signal window.</p>
      <div className="agent-acc-direction-grid">
        {summary.directionPerformance.map((row) => (
          <div key={row.direction} className="agent-acc-direction-card">
            <strong>{row.direction.toUpperCase()}</strong>
            <span>{formatSignalCount(row.total)} · {row.open} open</span>
            <span>{row.wins}W / {row.losses}L / {row.breakevens}BE</span>
            <span>{formatPct(row.winRate)} WR · {formatDollars(row.totalPnl)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SessionPerformanceSummary({ summary }: { summary: AgentAccountabilitySummary }) {
  return (
    <div className="agent-acc-session-perf" aria-label="Session performance">
      <div className="agent-acc-section-title">Session performance</div>
      <p>Read-only ORB split by trading session from the same scoped signal window.</p>
      <div className="agent-acc-session-grid">
        {summary.sessionPerformance.map((row) => (
          <div key={row.session} className="agent-acc-session-card">
            <strong>{row.label}</strong>
            <span>{formatSignalCount(row.total)} · {row.open} open</span>
            <span>{row.wins}W / {row.losses}L / {row.breakevens}BE</span>
            <span>{formatPct(row.winRate)} WR · {formatDollars(row.totalPnl)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function SymbolPerformanceSummary({ summary }: { summary: AgentAccountabilitySummary }) {
  return (
    <div className="agent-acc-symbol-perf" aria-label="Symbol performance">
      <div className="agent-acc-section-title">Symbol performance</div>
      <p>Read-only instrument split from the same scoped signal window.</p>
      <div className="agent-acc-detail-grid">
        {summary.symbolPerformance.map((row) => (
          <div key={row.symbol} className="agent-acc-detail-card">
            <strong>{row.symbol}</strong>
            <span>{formatSignalCount(row.total)} · {row.open} open</span>
            <span>{row.wins}W / {row.losses}L / {row.breakevens}BE</span>
            <span>{formatPct(row.winRate)} WR · {formatDollars(row.totalPnl)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function FbdTrapEvidence({ summary }: { summary: AgentAccountabilitySummary }) {
  const evidence = summary.fbdTrapEvidence;
  return (
    <div className="agent-acc-fbd-evidence" aria-label="FBD trap evidence">
      <div className="agent-acc-section-title">Trap evidence</div>
      <p>Read-only path mix and persisted trap scores. Volume-flow score remains LOG-ONLY.</p>
      <div className="agent-acc-evidence-grid">
        <div className="agent-acc-evidence-card">
          <strong>Path mix</strong>
          <span>{evidence.reclaimSignals} reclaim · {evidence.absorptionTrapSignals} absorption trap</span>
        </div>
        <div className="agent-acc-evidence-card">
          <strong>Trap score</strong>
          <span>{formatPct(evidence.avgTrapScore)} avg · {formatPct(evidence.maxTrapScore)} max</span>
          <small>{evidence.trapScoreCount} scored signals</small>
        </div>
        <div className="agent-acc-evidence-card">
          <strong>FBD flow score</strong>
          <span>{formatPct(evidence.avgFbdFlowScore)} avg</span>
          <small>{evidence.fbdFlowScoreCount} LOG-ONLY samples</small>
        </div>
      </div>
      <div className="agent-acc-section-title agent-acc-subsection-title">Path performance</div>
      <div className="agent-acc-detail-grid" aria-label="FBD path performance">
        {summary.fbdPathPerformance.map((row) => (
          <div key={row.path} className="agent-acc-detail-card">
            <strong>{row.label}</strong>
            <span>{formatSignalCount(row.total)} · {row.open} open</span>
            <span>{row.wins}W / {row.losses}L / {row.breakevens}BE</span>
            <span>{formatPct(row.winRate)} WR · {formatDollars(row.totalPnl)}</span>
            <small>{formatPct(row.avgTrapScore)} avg trap · {row.trapScoreCount} scored</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function MrReversionEvidence({ summary }: { summary: AgentAccountabilitySummary }) {
  const evidence = summary.mrReversionEvidence;
  return (
    <div className="agent-acc-mr-evidence" aria-label="MR reversion evidence">
      <div className="agent-acc-section-title">Reversion evidence</div>
      <p>Read-only MR extension and exhaustion snapshot. RR TP1 remains LOG-ONLY.</p>
      <div className="agent-acc-evidence-grid">
        <div className="agent-acc-evidence-card">
          <strong>Extension</strong>
          <span>{formatSigma(evidence.avgExtensionStddev)} avg · {formatSigma(evidence.maxExtensionStddev)} max</span>
          <small>{evidence.extensionStddevCount} sigma samples</small>
        </div>
        <div className="agent-acc-evidence-card">
          <strong>Confirmation</strong>
          <span>{formatPct(evidence.avgConfirmationScore)} avg</span>
          <small>{evidence.confirmationScoreCount} scored signals</small>
        </div>
        <div className="agent-acc-evidence-card">
          <strong>Exhaustion</strong>
          <span>{formatPct(evidence.avgExhaustionScore)} avg · {evidence.volumeExhaustionSignals} volume flags</span>
          <small>{evidence.exhaustionScoreCount} exhaustion samples</small>
        </div>
        <div className="agent-acc-evidence-card">
          <strong>RR TP1</strong>
          <span>{formatRMultiple(evidence.avgRrTp1)} avg</span>
          <small>{evidence.rrTp1Count} LOG-ONLY samples</small>
        </div>
      </div>
      <div className="agent-acc-section-title agent-acc-subsection-title">Extension performance</div>
      <div className="agent-acc-detail-grid" aria-label="MR extension performance">
        {summary.mrExtensionPerformance.map((row) => (
          <div key={row.bucket} className="agent-acc-detail-card">
            <strong>{row.label}</strong>
            <span>{formatSignalCount(row.total)} · {row.open} open</span>
            <span>{row.wins}W / {row.losses}L / {row.breakevens}BE</span>
            <span>{formatPct(row.winRate)} WR · {formatDollars(row.totalPnl)}</span>
            <small>{formatSigma(row.avgExtensionStddev)} avg extension · {row.extensionCount} samples</small>
          </div>
        ))}
      </div>
    </div>
  );
}

export function AgentAccountabilityPanel({
  state,
  config,
}: {
  state: QueryState<ExecutionSignal[]>;
  config: AgentAccountabilityConfig;
}) {
  if (state.status === "loading") {
    return <LoadingState label={`${config.label} accountability`} block />;
  }

  if (state.status === "error") {
    return <ErrorState error={state.error} onRetry={state.retry} label={`${config.label} accountability`} block />;
  }

  const summary = buildAgentAccountabilitySummary(state.data ?? [], config.agent);
  if (summary.total === 0) {
    return <EmptyState message={`No recent ${config.label} signals available yet.`} block />;
  }

  return (
    <section className="agent-acc-panel" aria-label={`${config.label} accountability`}>
      <div className="agent-acc-head">
        <div>
          <h3>{config.label} accountability</h3>
          <p>{config.description} Source: {config.sourceLabel ?? "getRecentSignals limit 30"}.</p>
        </div>
        <span>Read-only</span>
      </div>

      <div className="agent-acc-grid">
        <SummaryMetric label="Recent signals" value={summary.total} />
        <SummaryMetric label="Open" value={summary.open} />
        <SummaryMetric label="Closed" value={summary.closed} />
        <SummaryMetric label="Win rate" value={formatPct(summary.winRate)} />
        <SummaryMetric label="Record" value={`${summary.wins}W / ${summary.losses}L / ${summary.breakevens}BE`} />
        <SummaryMetric label="Realized P&L" value={formatDollars(summary.totalPnl)} />
        <SummaryMetric label="Direction split" value={`${summary.longs} long / ${summary.shorts} short`} />
        <SummaryMetric label="Session split" value={`${summary.asia} Asia / ${summary.us} US`} />
      </div>
      {config.showDirectionPerformance ? <DirectionPerformanceSummary summary={summary} /> : null}
      {config.showSessionPerformance ? <SessionPerformanceSummary summary={summary} /> : null}
      {config.showSymbolPerformance ? <SymbolPerformanceSummary summary={summary} /> : null}
      {config.showFbdTrapEvidence ? <FbdTrapEvidence summary={summary} /> : null}
      {config.showMrReversionEvidence ? <MrReversionEvidence summary={summary} /> : null}
      {config.showCandlePatterns ? (
        <CandlePatternSummary
          summary={summary}
          showPerformance={config.showCandlePatternPerformance}
          showSignalLedger={config.showCandlePatternSignalLedger}
        />
      ) : null}
    </section>
  );
}
